/**
 * @file Component to create grouped vertical bar chart
 */

import * as d3 from 'd3';
import { Stage } from '../common/Stage';
import { Axis } from '../common/Axis';
import { Tooltip } from '../common/Tooltip';
import { Legend } from '../common/Legend';
import { ColorScale } from '../common/ColorScale';
import { EmptyState } from '../../empty-state/EmptyState';

import { hasValues, toggleChartVisibility } from '../common/utils';

export class VerticalBarChartGrouped {

  /**
   * Setup chart properties
   * @param {Object} props - The properties to initialize the stage
   * @param {Object} props.data - The data necessary to draw the chart
   * @param {HTMLElement} props.element - The DOM element to append the chart
   * @param {number} [props.height] - The height of the chart
   * @param {Object} [props.margin] - The top, right, bottom and left margins
   * @param {number} [props.margin.top] - The top margin
   * @param {number} [props.margin.right] - The right margin
   * @param {number} [props.margin.bottom] - The bottom margin
   * @param {number} [props.margin.left] - The left margin
   * @param {string} [props.y] - The Y axis properties
   * @param {string} [props.y.unit] - The unit of the Y axis
   * @param {function} [props.onSelect] - A method to react to serie selections.
   * @param {Object} props.error - Empty state settings
   */

  constructor(props) {
    this.data = props.data;
    this.element = props.element;
    this.margin = {
      left: 32,
      right: 24,
      top: 24,
      bottom: 24,
      ...props.margin
    };

    this.height = props.height || 250;
    this.thresholds = props.thresholds;
    this.y = { unit: '', ...props.y };

    this.colorScale = props.colorScale || this._newColorScale();

    this.axisPadding = 8;
    this.fontSize = 14;
    this.barWidth = 6;
    this.animationSpeed = 400;

    if (this.y.unit) {
      this.margin.top += this.fontSize + this.axisPadding;
    }

    this.emptyState = props.error || null;

    this.onSelect = props.onSelect || (() => {});
  }

  /**
   * Creates a new ColorScale using the chart data
   * @return - A new ColorScale
   */
  _newColorScale() {
    const colorScale = new ColorScale({
      data: this.data
    });
    colorScale.init();
    return colorScale;
  }

  /**
   * Initialize the chart
   */

  init() {
    this.stage = new Stage({
      element: this.element,
      margin: this.margin,
      height: this.height
    });

    this.stage.init();
    this.stage.onResize(this.draw.bind(this));

    this.canvas = this.stage.getCanvas();

    this._initLegend();
    this._initTooltip();
    this._initEmptyState();

    this.draw();
  }

  /**
   * Destroy the cart
   */

  destroy() {
    this.stage.destroy();
  }

  _initLegend() {
    this.legend = new Legend({
      element: this.element,
      data: this.data,
      draw: this.draw.bind(this),
      highlight: this._highlight.bind(this),
      margin: { ...this.margin, left: 0 },
      colorScale: this.colorScale
    });
    this.legend.init();
  }

  _initTooltip() {
    this.tooltip = new Tooltip({
      element: this.element,
      data: this.data,
      unit: {
        value: this.y.unit
      },
      hoverClass: 'line-hover',
      colorScale: this.colorScale
    });

    this.tooltip.init();
  }

  _initEmptyState() {
    this.emptyStateBox = new EmptyState({
      element: this.element,
      height: this.element.offsetHeight,
      border: true,
      settings: this.emptyState
    });
    this.emptyStateBox.init();
  }

  /**
   * Draw the chart based on data
   * @param {Object} data - The data necessary to draw the chart
   */

  draw(data) {
    this.data = data || this.data;

    if (!this.legend.pills.length) {
      d3.select(this.element).selectAll('.chart-legend').remove();

      this._initLegend();
    }

    this.data = this.legend.filter(this.data);
    this.width = this.stage.getCanvasWidth();

    toggleChartVisibility(this.element, hasValues(this.data));

    if (!hasValues(this.data)) {
      return;
    }

    const x0 = d3.scaleBand()
      .domain(this.data.common)
      .rangeRound([0, this.width])
      .paddingInner(0.7)
      .paddingOuter(0.3);

    const x1 = d3.scaleBand()
      .domain(this.data.series.map(d => d.name))
      .rangeRound([-x0.bandwidth(), x0.bandwidth()])
      .padding(1);

    const yMin = d3.min(this.data.series, d => d3.min(d.values)) >= 0 ? 0 :
      d3.min(this.data.series, d => d3.min(d.values));
    const yMax = d3.max(this.data.series, d => d3.max(d.values)) <= 0 ? 0 :
      d3.max(this.data.series, d => d3.max(d.values));
    const y = d3.scaleLinear()
      .domain([yMin, yMax])
      .nice()
      .rangeRound([this.height, 0]);

    const axis = new Axis({
      position: 'left',
      canvas: this.canvas,
      y: y,
      width: this.width,
      height: this.height,
      margin: this.margin,
      fontSize: this.fontSize,
      axisPadding: this.axisPadding,
      ticks: this.height / 80,
      tickFormat: (d) => d3.format('.2s')(d).replace('.0', ''),
      unit: this.y.unit
    });

    axis.init();

    let hasLabelSpace = true;
    let hasNegativeValues = false;
    let seriesDistance;

    if (this.data.series.length > 0) {
      if (this.data.common.length > 1) {
        seriesDistance = x0(this.data.common[1]) - x0(this.data.common[0]);
      } else {
        seriesDistance = this.width;
      }

      this.data.common.forEach(d => {
        if (hasLabelSpace) {
          hasLabelSpace = d.length * this.fontSize / 1.5 < seriesDistance;
        }
      });

      this.data.series.forEach(d => {
        if (!hasNegativeValues && d.values.filter(i => i < 0).length > 0) {
          hasNegativeValues = true;
        }
      });
    }

    const barGroups = this.canvas
      .classed('bar-chart', true)
      .selectAll('.bar-group')
      .data(this.data.common);

    const barGroupsEnter = barGroups
      .enter()
      .append('g')
      .classed('bar-group', true);

    const labelPosition = selection => {
      selection
        .transition()
        .duration(this.animationSpeed)
        .attr('y', hasNegativeValues ? this.height - y(0) + this.margin.bottom - 2 : this.fontSize + 2);
    };

    barGroupsEnter
      .append('text')
      .classed('label', true)
      .classed('hidden', !hasLabelSpace)
      .attr('text-anchor', 'middle')
      .style('pointer-events', 'none')
      .text(d => d)
      .call(labelPosition);

    barGroups
      .selectAll('.label')
      .call(labelPosition);

    barGroups
      .selectAll('.line-hover')
      .attr('stroke-width', seriesDistance);

    barGroupsEnter
      .merge(barGroups)
      .transition()
      .duration(this.animationSpeed)
      .attr('transform', d => `translate(${x0(d) + x0.bandwidth() / 2 + 1}, ${y(0, d)})`);

    const bars = barGroupsEnter
      .merge(barGroups)
      .selectAll('.bar')
      .data(
        (d, i) => this.data.series.map(serie => ({ key: serie.name, value: serie.values[i] })),
        d => d.key
      );

    bars
      .enter()
      .append('line')
      .merge(bars)
      .style('pointer-events', 'none')
      .attr('stroke-width', this.barWidth)
      .attr('class', d => 'bar ' + this.colorScale.get()(d.key))
      .transition()
      .duration(this.animationSpeed)
      .attr('x1', d => x1(d.key))
      .attr('x2', d => x1(d.key))
      .attr('y1', 0)
      .attr('y2', d => isNaN(d.value) ? 0 : y(d.value) - y(0));

    barGroups
      .exit()
      .remove();

    bars
      .exit()
      .transition()
      .duration(this.animationSpeed)
      .attr('y2', 0)
      .remove();

    if (this.data.series.length > 0) {
      barGroupsEnter
        .append('line')
        .classed('line-hover', true)
        .attr('stroke-width', seriesDistance)
        .attr('stroke', 'blue')
        .attr('opacity', '0')
        .attr('y1', -this.height)
        .attr('y2', this.height)
        .on('mouseover', (group, i, selection) => {
          const all = barGroupsEnter.selectAll('.bar');
          const allLabels = barGroupsEnter.selectAll('.label');

          this.tooltip.update(i, this.data);

          all.attr('opacity', '.2');
          allLabels.attr('opacity', '.2');

          d3.select(selection[i].parentNode).selectAll('.bar').attr('opacity', '1');
          d3.select(selection[i].parentNode).selectAll('.label').attr('opacity', '1');
        })
        .on('mouseout', () => {
          const all = barGroupsEnter.selectAll('.bar');
          const allLabels = barGroupsEnter.selectAll('.label');

          all.attr('opacity', '1');
          allLabels.attr('opacity', '1');
        })
        .on('click', d => this.onSelect(d));
    }
  }

  /**
   * Highlight one common element in all series
   * @param {String} key - The key matching the common element
   * @private
   */

  _highlight(key) {
    const bars = this.canvas
      .selectAll('.bar');

    if (key) {
      bars.attr('opacity', d => d.key === key ? '1' : '.2');
    } else {
      bars.attr('opacity', '1');
    }
  }
}
